import ProductReview from '../../domains/ProductReview'
import PubSub from '../PubSub'

/**
 * PL-only photo-popup state manager. A copy of the legacy ReviewPopupStateManager, kept separate so
 * the PL reviews surface can add pagination (hasMore + a next-page loader) WITHOUT touching the
 * shared legacy manager/popups. The PL gallery + list drive this; the PL popups read it.
 */
export interface PLReviewPopupState {
  showPopUp: boolean
  productReviews: ProductReview[]
  reviewId: number
  imageIndex: number
  // True while more review pages remain. Serializable, so it survives the deep-clone useAppSelector
  // performs on every publish (a function would be stripped — hence fetchMore lives on the instance).
  hasMore: boolean
}

interface SetPLReviewPopupPayload {
  productReviews: ProductReview[]
  reviewId: number
  imageIndex: number
  hasMore?: boolean
  // Next-page loader. Stored on the instance — NOT in `state` — because useAppSelector deep-clones
  // state on every publish, which would strip a function. May be async (the gallery's loader awaits a
  // fetch); the return is not consumed, so both sync and Promise-returning loaders are accepted.
  fetchMore?: () => void | Promise<void>
}

class PLReviewPhotoPopupStateManager extends PubSub {
  private state: PLReviewPopupState
  // Lives outside `state` so it isn't deep-cloned away; invoked via triggerFetchMore().
  private fetchMoreHandler?: () => void | Promise<void>

  constructor() {
    super()
    this.state = { showPopUp: false, productReviews: [], reviewId: 0, imageIndex: 0, hasMore: false }
  }

  showReviewPopUp = (payload: SetPLReviewPopupPayload) => {
    this.state.showPopUp = true
    this.state.productReviews = payload.productReviews
    this.state.reviewId = payload.reviewId
    this.state.imageIndex = payload.imageIndex
    this.state.hasMore = payload.hasMore ?? false
    this.fetchMoreHandler = payload.fetchMore
    this.publish()
  }

  hideReviewPopUp = () => {
    this.state.showPopUp = false
    this.state.productReviews = []
    this.state.reviewId = 0
    this.state.imageIndex = 0
    this.state.hasMore = false
    this.fetchMoreHandler = undefined
    this.publish()
  }

  // Append a freshly-fetched page (new array ref so the popup's rebuild effect re-runs) and update
  // the hasMore gate. Called by the PL gallery widget after it loads the next page for the popup.
  appendReviews = (reviews: ProductReview[], hasMore: boolean) => {
    this.state.productReviews = [...this.state.productReviews, ...reviews]
    this.state.hasMore = hasMore
    this.publish()
  }

  setHasMore = (hasMore: boolean) => {
    this.state.hasMore = hasMore
    this.publish()
  }

  // Invoked by the popup when the user nears the last loaded review; forwards to the registered loader.
  // The loader may be async; its promise isn't awaited here (fire-and-forget), hence the explicit void.
  triggerFetchMore = () => {
    void this.fetchMoreHandler?.()
  }

  getReviewPopupState = () => {
    return this.state
  }
}

const plReviewPhotoPopupStateManager: PLReviewPhotoPopupStateManager =
  PLReviewPhotoPopupStateManager.getInstance('plReviewPhotoPopupStateManager')

export default plReviewPhotoPopupStateManager
